import api from ".";
import generateHeaders from "../utils/generateHeaders.js";

/**
 * @name buildEventIdsParam
 * @param {Array<number>} eventIds
 * @returns {string|undefined}
 * @description INFORMACP-4684 - formato canonico de eventIds no GET: lista separada por virgula.
 * "Todos" e representado pela AUSENCIA do parametro (undefined), nunca por valor vazio, para nao
 * depender de como axios, Spring MVC e API Gateway tratam string vazia.
 */
const buildEventIdsParam = (eventIds) => {
  if (!Array.isArray(eventIds) || eventIds.length === 0) {
    return undefined;
  }

  return eventIds.join(",");
};

export const getDocumentsAnalyticsService = async ({
  token,
  year,
  eventIds,
  page,
  size,
  sortBy,
  sortDirection,
  document,
}) => {
  const headers = generateHeaders(token);

  const options = {
    ...headers,
    params: {
      page,
      size,
      sortBy,
      sortDirection,
      year,
      eventIds: buildEventIdsParam(eventIds),
      document,
    },
  };

  return await api.get("/upload-flow/analytics", options);
};

/**
 * @name getAnalyticsFiltersService
 * @param {Object} params
 * @param {string} params.token - Token de autenticação
 * @param {number|null} params.year - Ano da edição (opcional)
 * @param {Array<number>} params.eventIds - Eventos selecionados (opcional, vazio = todos)
 * @returns {Promise} Response com years, events e documents
 * @description Filtros da tela de analytics. Sem ano, devolve apenas os anos disponíveis. Com ano,
 * devolve também os eventos elegíveis do ano e os documentos vinculados a eles.
 */
export const getAnalyticsFiltersService = async ({ token, year, eventIds }) => {
  const headers = generateHeaders(token);

  return await api.get("/upload-flow/analytics/filters", {
    ...headers,
    params: {
      year,
      eventIds: buildEventIdsParam(eventIds),
    },
  });
};

/**
 * @name createAnalyticsReportService
 * @param {Object} params
 * @param {string} params.token - Token de autenticação
 * @param {number} params.year - Ano da edição (obrigatório)
 * @param {Array<number>} params.eventIds - Eventos selecionados (vazio = todos os do ano)
 * @param {string} params.document - Documento para filtro (opcional)
 * @returns {Promise} Response com reportId
 * @description Cria um relatório de analytics para download assíncrono
 */
export const createAnalyticsReportService = async ({
  token,
  year,
  eventIds,
  document,
}) => {
  const headers = generateHeaders(token);

  const data = {
    year: year || null,
    eventIds: Array.isArray(eventIds) && eventIds.length > 0 ? eventIds : null,
    document: document || null,
  };

  return await api.post("/async/upload-flow-analytics-report", data, headers);
};

/**
 * @name getAnalyticsReportStatusService
 * @param {Object} params
 * @param {string} params.token - Token de autenticação
 * @param {string} params.reportId - ID do relatório
 * @returns {Promise} Response com status e downloadUrl
 * @description Verifica o status de um relatório de analytics
 */
export const getAnalyticsReportStatusService = async ({ token, reportId }) => {
  const headers = generateHeaders(token);

  return await api.get(
    `/async/upload-flow-analytics-report/${reportId}`,
    headers,
  );
};
